import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLine, EASE } from "./Motion";

const SWIRL =
    "https://images.pexels.com/photos/36957437/pexels-photo-36957437.jpeg?auto=compress&cs=tinysrgb&w=1600";

const SERVICE_INDEX = [
    "Media Planning & Buying",
    "Digital Marketing",
    "Social Media",
    "PR & Communications",
    "Business Consultation",
    "DAAS · Data As A Service",
];

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [0.35, 0]);

    return (
        <section ref={ref} data-testid="hero" className="relative flex min-h-screen flex-col justify-end overflow-hidden px-5 md:px-10 pt-28 pb-8">
            <motion.div
                style={{ y, opacity }}
                aria-hidden="true"
                className="pointer-events-none absolute -right-[15%] -top-[10%] w-[70vw] max-w-[900px] mix-blend-screen [mask-image:radial-gradient(closest-side,black_35%,transparent_98%)]"
            >
                <img src={SWIRL} alt="" className="h-auto w-full opacity-60" loading="eager" />
            </motion.div>

            <div className="relative z-10">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="mb-6 md:mb-10 flex items-center gap-3"
                >
                    <span className="h-2 w-2 rounded-full bg-vermilion animate-pulse" />
                    <p className="eyebrow">HVP Consultants — Marketing &amp; Business Consultancy</p>
                </motion.div>

                <h1 className="display-hero font-display font-light text-paper">
                    <MaskedLine delay={0.25}>WE TURN</MaskedLine>
                    <MaskedLine delay={0.4}>
                        <span>
                            <em className="italic font-light text-vermilion">NOISE</em> INTO
                        </span>
                    </MaskedLine>
                    <MaskedLine delay={0.55}>
                        <span>
                            FAME<span className="text-vermilion">.</span>
                        </span>
                    </MaskedLine>
                </h1>

                <div className="mt-10 md:mt-14 grid gap-10 md:grid-cols-12 md:items-end">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                        className="md:col-span-5 max-w-md text-base md:text-lg font-light leading-relaxed text-smoke"
                        data-testid="hero-subline"
                    >
                        HVP Consultants plans media, drives digital, owns social, earns press
                        and turns data into decisions — integrated campaigns for brands that
                        refuse to blend in.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                        className="md:col-span-4 flex flex-wrap gap-4"
                    >
                        <button
                            data-testid="hero-cta-start"
                            onClick={() => window.__lenis ? window.__lenis.scrollTo("#contact", { offset: -70 }) : document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                            className="group flex items-center gap-3 bg-vermilion px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-vermilion-hover"
                        >
                            Start a project
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        <Link
                            data-testid="hero-cta-work"
                            to="/work"
                            className="flex items-center gap-3 border border-line px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:border-vermilion hover:text-vermilion"
                        >
                            See the work
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.2 }}
                        className="hidden md:flex md:col-span-3 flex-col items-end gap-3 text-right"
                    >
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-smoke/70">
                            Scroll
                        </span>
                        <ArrowDown className="h-4 w-4 text-vermilion animate-bounce" />
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.3 }}
                    className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-3 border-t border-line"
                    data-testid="hero-service-index"
                >
                    {SERVICE_INDEX.map((s, i) => (
                        <div
                            key={s}
                            className="flex items-center gap-3 border-b border-r border-line px-4 py-4 max-md:[&:nth-child(2n)]:border-r-0 max-md:[&:nth-last-child(-n+2)]:border-b-0 md:border-b-0 md:[&:nth-child(3n)]:border-r-0"
                        >
                            <span className="font-mono text-[10px] text-vermilion">0{i + 1}</span>
                            <span className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-smoke">
                                {s}
                            </span>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
